
import "../css/admin.css";


import { useEffect, useState, useRef } from "react";

import { fromCamelCaseToCapitalizedSpace } from "../common/helpers";
import { ENVIRONMENT, QA, AI4YOUTH, ENRICHYA, PROD, DEV, DEBUG, SERVER_ORIGIN } from "../common/constants";

import { useAdmin, useAdminUpdate } from "../context/AdminContext";
import { PromptTemplateType, usePromptUpdate } from "../context/PromptContext";




function AdminToggle() {
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const { toggleAdminMode } = useAdminUpdate();

    if (ENVIRONMENT === PROD) {
        return null;
    }

    return (
        <div
            className="admin-toggle"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <button
                onClick={toggleAdminMode}
                className={`admin-toggle-button ${isHovered ? "admin-toggle-button-hover" : ""}`}
            >
                ⚙️
            </button>
            {isHovered && (
                <div className="admin-tooltip">
                    Admin Panel
                </div>
            )}
        </div>
    );
}



function Overlay({ onClose, children }: { onClose: () => void, children: React.ReactNode }) {

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if( e.key === "Escape" ) {
                onClose();
            }
        };

        document.addEventListener( "keydown", handleKeyDown );

        return () => document.removeEventListener( "keydown", handleKeyDown );
    }, [onClose]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if( e.target === e.currentTarget ) {
            onClose();
        }
    };

    return (
        <div className="overlay-container" onClick={handleOverlayClick}>
            {children}
        </div>
    );
}

function AdminOverlay() {
    const { toggleAdminMode } = useAdminUpdate();

    return (
        <Overlay onClose={toggleAdminMode}>
            <div className="admin-panel-container">
                <button
                    onClick={toggleAdminMode}
                    className="admin-close-button"
                >
                    ✕
                </button>
                <AdminPanel />
            </div>
        </Overlay>
    );
}

function AdminPanel() {

    const { selectedSection, selectedSubsection, sections, selectedPlatform, selectedEnvironment } = useAdmin();
    const { changeSelectedSection, changeSelectedSubsection, changeSelectedPlatform, changeSelectedEnvironment } = useAdminUpdate();
    const { getAllPrompts, savePromptsToDatabase } = usePromptUpdate();

    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [saveMessage, setSaveMessage] = useState<string>('');

    // Safety check: If DEBUG is false and environment is "dev", switch to QA
    useEffect(() => {
        if (!DEBUG && selectedEnvironment === DEV) {
            changeSelectedEnvironment(QA);
        }
    }, [selectedEnvironment, changeSelectedEnvironment]);

    const handleSave = async () => {
        setSaveStatus('saving');
        setSaveMessage('');

        try {
            const success = await savePromptsToDatabase(selectedPlatform, selectedEnvironment);

            if (success) {
                setSaveStatus('success');
                setSaveMessage('Prompts saved successfully!');
            } else {
                setSaveStatus('error');
                setSaveMessage('Failed to save prompts. Please try again.');
            }
        } catch (error) {
            console.error('Error saving prompts:', error);
            setSaveStatus('error');
            setSaveMessage('Error saving prompts. Check console for details.');
        }

        // Clear status after 3 seconds
        setTimeout(() => {
            setSaveStatus('idle');
            setSaveMessage('');
        }, 3000);
    };

    return (
        <div className="admin-panel">
            <div className="admin-sidebar">
                <h2 className="admin-sidebar-title">Configuration</h2>

                {/* Platform and Environment Selectors */}
                <div className="admin-platform-env-selectors" style={{ marginBottom: '20px', padding: '10px', borderBottom: '1px solid #ddd' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>Platform:</label>
                        <select
                            value={selectedPlatform}
                            onChange={(e) => changeSelectedPlatform(e.target.value)}
                            className="admin-section-dropdown"
                        >
                            <option value={AI4YOUTH}>AI4Youth</option>
                            <option value={ENRICHYA}>Enrichya</option>
                        </select>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>Environment:</label>
                        <select
                            value={selectedEnvironment}
                            onChange={(e) => changeSelectedEnvironment(e.target.value)}
                            className="admin-section-dropdown"
                        >
                            {DEBUG && <option value={DEV}>Dev (Local)</option>}
                            <option value={QA}>QA</option>
                            <option value={PROD}>Production</option>
                        </select>
                    </div>

                    {/* Save Button */}
                    <button
                        onClick={handleSave}
                        disabled={saveStatus === 'saving'}
                        className="admin-save-button"
                        style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: saveStatus === 'success' ? '#4CAF50' : saveStatus === 'error' ? '#f44336' : '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? '✓ Saved!' : saveStatus === 'error' ? '✗ Error' : '💾 Save Changes'}
                    </button>

                    {/* Save Status Message */}
                    {saveMessage && (
                        <div style={{
                            marginTop: '8px',
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: saveStatus === 'success' ? '#e8f5e9' : '#ffebee',
                            color: saveStatus === 'success' ? '#2e7d32' : '#c62828'
                        }}>
                            {saveMessage}
                        </div>
                    )}

                    {/* Editing Context Indicator */}
                    <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        backgroundColor: DEBUG ? '#e3f2fd' : '#f5f5f5',
                        color: '#666',
                        border: DEBUG ? '1px solid #2196F3' : 'none'
                    }}>
                        {DEBUG && <div style={{ fontSize: '10px', color: '#2196F3', marginBottom: '4px' }}>🔧 DEBUG MODE</div>}
                        Editing: <strong>{selectedPlatform}/{selectedEnvironment}</strong>
                    </div>
                </div>

                <div className="admin-section-selector">
                    <select
                        value={selectedSection}
                        onChange={(e) => {
                            changeSelectedSection(e.target.value);
                            changeSelectedSubsection(Object.keys(sections[e.target.value].subsections)[0]);
                        }}
                        className="admin-section-dropdown"
                    >
                        {Object.values(sections).map((section) => (
                            <option key={section.id} value={section.id}>
                                {section.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="admin-subsection-nav">
                    {Object.entries(sections[selectedSection].subsections).map(([key, label]) => (
                        <button
                        key={key}
                        onClick={() => changeSelectedSubsection(key)}
                        className={`admin-sidebar-button ${
                            selectedSubsection === key ? 'admin-sidebar-button-active' : ''
                        }`}
                        >
                        ▸ {fromCamelCaseToCapitalizedSpace(label)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="admin-content">
                <AdminPanelContent section={selectedSection} subsection={selectedSubsection} />
            </div>
        </div>
    );
}






/* ============================== HELPERS ============================== */


interface ContentRendererProps {
    children: React.ReactNode;
    title: string;
    description?: string;
}
function ContentRenderer({ title, description, children }: ContentRendererProps) {
    return (
        <div className="admin-content-section">
            <h3 className="admin-content-title">{title}</h3>
            {description && <p className="admin-content-description">{description}</p>}
            {children}
        </div>
    );
}


// ============================== PROMPTS ==============================
function PromptsContent({ subsection }: { subsection: string }) {
    const { getPromptTemplatesForCategory, updatePromptTemplate } = usePromptUpdate();
    const [localTemplates, setLocalTemplates] = useState<Record<string, string>>({});
    const updateTimers = useRef<Record<string, NodeJS.Timeout>>({});

    // Initialize local state from context when subsection changes
    useEffect(() => {
        const templates = getPromptTemplatesForCategory(subsection);
        const initial: Record<string, string> = {};
        templates.forEach(t => {
            initial[t.id] = t.template;
        });
        setLocalTemplates(initial);
    }, [subsection, getPromptTemplatesForCategory]);

    const handleChange = (id: string, value: string) => {
        // Update local state immediately for responsive UI
        setLocalTemplates(prev => ({ ...prev, [id]: value }));

        // Clear existing timer for this field
        if (updateTimers.current[id]) {
            clearTimeout(updateTimers.current[id]);
        }

        // Debounce update to context (500ms)
        updateTimers.current[id] = setTimeout(() => {
            updatePromptTemplate(id, value);
        }, 500);
    };

    return (
        <ContentRenderer
            title={`Prompt Templates - ${fromCamelCaseToCapitalizedSpace(subsection)}`}
            description="Customize the AI's behavior by modifying these prompt templates."
        >
            {getPromptTemplatesForCategory(subsection).map((promptTemplate) => (
                <div key={promptTemplate.id} className="admin-input-container">
                    <label className="admin-input-label">
                        {promptTemplate.name}
                        {promptTemplate.description && (
                            <span className="admin-input-description">{promptTemplate.description}</span>
                        )}
                    </label>
                    <textarea
                        value={localTemplates[promptTemplate.id] ?? promptTemplate.template}
                        onChange={(e) => handleChange(promptTemplate.id, e.target.value)}
                        className="admin-input-textarea"
                    />
                </div>
            ))}
        </ContentRenderer>
    );
}


// ========================== GENERATED PROMPTS ===========================
function PromptsGeneratorContent({ subsection }: { subsection: string }) {

    const { getPromptTemplatesForCategory, updatePromptTemplate } = usePromptUpdate();
    const [localTemplates, setLocalTemplates] = useState<Record<string, string>>({});
    const updateTimers = useRef<Record<string, NodeJS.Timeout>>({});

    // Initialize local state from context when subsection changes
    useEffect(() => {
        const templates = getPromptTemplatesForCategory(subsection, PromptTemplateType.GENERATED);
        const initial: Record<string, string> = {};
        templates.forEach(t => {
            initial[t.id] = t.template;
        });
        setLocalTemplates(initial);
    }, [subsection, getPromptTemplatesForCategory]);

    const handleChange = (id: string, value: string) => {
        // Update local state immediately for responsive UI
        setLocalTemplates(prev => ({ ...prev, [id]: value }));

        // Clear existing timer for this field
        if (updateTimers.current[id]) {
            clearTimeout(updateTimers.current[id]);
        }

        // Debounce update to context (500ms)
        updateTimers.current[id] = setTimeout(() => {
            updatePromptTemplate(id, value, PromptTemplateType.GENERATED);
        }, 500);
    };

    return (
        <ContentRenderer
            title={`Generated Prompts - ${fromCamelCaseToCapitalizedSpace(subsection)}`}
            description="Generated prompts are automatically generated by the AI."
        >
            {getPromptTemplatesForCategory(subsection, PromptTemplateType.GENERATED).map((promptTemplate) => (
                <div key={promptTemplate.id} className="admin-input-container">
                    <label className="admin-input-label">
                        {promptTemplate.name}

                        {/* Icon Button with Tooltip */}
                        {promptTemplate.response && (
                            <div className="tooltip-container">
                                <button className="info-icon-button" aria-label="View response details">
                                    ℹ️
                                </button>
                                <div className="tooltip">
                                    {promptTemplate.response}
                                </div>
                            </div>
                        )}

                        {promptTemplate.description && (
                            <span className="admin-input-description">{promptTemplate.description}</span>
                        )}
                    </label>
                    <textarea
                        value={localTemplates[promptTemplate.id] ?? promptTemplate.template}
                        onChange={(e) => handleChange(promptTemplate.id, e.target.value)}
                        className="admin-input-textarea"
                    />
                </div>
            ))}
        </ContentRenderer>
    );
}




// Main Content Router Component
interface SectionContentProps {
    section: string;
    subsection: string;
}
function AdminPanelContent({ section, subsection }: SectionContentProps) {
    const renderContent = () => {
        switch (section) {
            case "prompts":
                return <PromptsContent subsection={subsection} />;
            case "promptsGenerator":
                return <PromptsGeneratorContent subsection={subsection} />;
            default:
                return <div>Section not found</div>;
        }
    };

    return renderContent();
}








export {
    AdminToggle,
    AdminOverlay,
}

